import { Box, Tabs, TabList, Tab, Text, Flex } from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { portfolioOverviewBreadcrumb } from './PortfolioBreadcrumbs';
import { colors } from 'packages/common-frontend/colors';
import { Breadcrumbs } from 'packages/common-frontend/components/Breadcrumbs';
import { Icon } from 'packages/common-frontend/components/icons';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import {
  creditFacilityIdQueryParam,
  CreditFacilitySelectWithQueryParams,
} from 'packages/credit-facility/ui/CreditFacilitySelect';

export enum PortfolioTabOptionKeys {
  summary = 'summary',
  covenants = 'covenants',
  borrowingBase = 'borrowingBase',
  profitLoss = 'profitLoss',
  creditAgreement = 'creditAgreement',
  receivables = 'receivables',
  strats = 'strats',
  reportBuilder = 'reportBuilder',
}
type PortfolioTabType = {
  key: PortfolioTabOptionKeys;
  label: string;
  route: string;
};
const portfolioTabOptions: Record<PortfolioTabOptionKeys, PortfolioTabType> = {
  summary: {
    key: PortfolioTabOptionKeys.summary,
    label: 'Summary',
    route: `/portfolio/summary`,
  },
  covenants: {
    key: PortfolioTabOptionKeys.covenants,
    label: 'Covenants',
    route: '/portfolio/covenants',
  },
  borrowingBase: {
    key: PortfolioTabOptionKeys.borrowingBase,
    label: 'Borrowing Base',
    route: '/portfolio/borrowing-base',
  },
  profitLoss: {
    key: PortfolioTabOptionKeys.profitLoss,
    label: 'Profit & Loss',
    route: '/portfolio/profit-loss',
  },
  receivables: {
    key: PortfolioTabOptionKeys.receivables,
    label: 'Receivables',
    route: '/portfolio/receivables',
  },
  creditAgreement: {
    key: PortfolioTabOptionKeys.creditAgreement,
    label: 'Credit Agreement',
    route: '/portfolio/credit-agreement',
  },
  strats: {
    key: PortfolioTabOptionKeys.strats,
    label: 'Strats',
    route: '/portfolio/strats',
  },
  reportBuilder: {
    key: PortfolioTabOptionKeys.reportBuilder,
    label: 'Report Builder',
    route: '/portfolio/report-builder',
  },
};

export const PortfolioTemplate: React.FC<{
  organizationId: string;
  creditFacilitySlug: string;
  selectedTab: PortfolioTabOptionKeys;
  children: React.ReactNode;
}> = withLDConsumer()(
  ({ organizationId, creditFacilitySlug, selectedTab, children, flags }) => {
    const history = useHistory();
    const query = useQueryParams();
    const currentCreditFacilityId = query.get(creditFacilityIdQueryParam);

    // can configure feature flags for specific tabs as needed in the future
    // to conditionally render tabs
    const tabs = useMemo<PortfolioTabType[]>(() => {
      const tabsToDisplay = [
        portfolioTabOptions.summary,
        portfolioTabOptions.borrowingBase,
        ...(flags?.displayLenderPortfolioMonitoringCovenants
          ? [portfolioTabOptions.covenants]
          : []),
        portfolioTabOptions.profitLoss,
        ...(flags?.displayLenderPortfolioMonitoringReceivables
          ? [portfolioTabOptions.receivables]
          : []),
        portfolioTabOptions.creditAgreement,
        ...(flags?.showLenderPortfolioStrats &&
        flags?.showLenderPortfolioStrats !== 'hide'
          ? [portfolioTabOptions.strats]
          : []),
        ...(flags?.showExploReportBuilder
          ? [portfolioTabOptions.reportBuilder]
          : []),
      ];

      return tabsToDisplay;
    }, [flags]);

    const breadcrumbs = [
      portfolioOverviewBreadcrumb,
      {
        label: commonUtils.creditFacilitySlugToName(creditFacilitySlug),
      },
    ];

    const onSelectRedirectLink = (creditFacilityId: string) => {
      const baseRoute = portfolioTabOptions[selectedTab].route;
      const queryParams = `?${creditFacilityIdQueryParam}=${creditFacilityId}`;
      return `${baseRoute}${queryParams}`;
    };

    return (
      <Box>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <Box width="50%" mt="32px">
          <CreditFacilitySelectWithQueryParams
            displayInputFloatingLabel
            inputFloatingLabelBackgroundColor="white"
            organizationId={organizationId}
            onSelectRedirectLink={onSelectRedirectLink}
          />
        </Box>
        <Tabs
          variant="line"
          index={tabs.findIndex(({ key }) => key === selectedTab)}
          colorScheme="blue"
          background="white"
        >
          <TabList>
            {tabs.map(({ route, label, key }) => (
              <Tab
                key={key}
                onClick={() => {
                  history.push(
                    `${route}?${creditFacilityIdQueryParam}=${currentCreditFacilityId}`,
                  );
                }}
                fontSize="sm"
              >
                {label}
              </Tab>
            ))}
          </TabList>
        </Tabs>
        <Box mt="32px">{children}</Box>
      </Box>
    );
  },
);

export const PortfolioGridItem: React.FC<{
  label: string;
  value?: string;
  backgroundColor?: string;
  isActive?: boolean;
  cursor?: string;
}> = ({
  label,
  value = '-',
  backgroundColor = 'white',
  isActive = false,
  cursor = 'default',
}) => {
  return (
    <Box
      padding="16px 24px"
      backgroundColor={backgroundColor}
      borderWidth="1px"
      borderColor={isActive ? colors.black[70] : colors.gray[200]}
      borderRadius="8px"
      cursor={cursor}
    >
      <Text fontSize="xs" marginBottom="8px" color={colors.gray[500]}>
        {label}
      </Text>
      <Text fontWeight="bold" color={colors.black[95]}>
        {value}
      </Text>
    </Box>
  );
};

export const BooleanIconGridItem: React.FC<{
  label: string;
  value: boolean;
  backgroundColor?: string;
  isActive?: boolean;
  cursor?: string;
  defaultTrueDisplayValue?: string;
  defaultFalseDisplayValue?: string;
}> = ({
  label,
  value,
  backgroundColor = 'white',
  isActive = false,
  cursor = 'default',
  defaultTrueDisplayValue = 'True',
  defaultFalseDisplayValue = 'False',
}) => {
  return (
    <Box
      padding="16px 24px"
      backgroundColor={backgroundColor}
      borderWidth="1px"
      borderColor={isActive ? colors.black[70] : colors.gray[200]}
      borderRadius="8px"
      cursor={cursor}
    >
      <Text fontSize="xs" marginBottom="8px" color={colors.gray[500]}>
        {label}
      </Text>
      <Flex alignItems="center">
        {value === true ? (
          <Icon icon="circle-check" color={colors.success} size="large" />
        ) : (
          <Icon icon="circle-x" color={colors.error} size="large" />
        )}
        <Text fontWeight="bold" color={colors.black[95]} ml="16px">
          {value === true ? defaultTrueDisplayValue : defaultFalseDisplayValue}
        </Text>
      </Flex>
    </Box>
  );
};
